import { useEffect, useState } from "react";
import DashboardShell from "../components/DashboardShell";
import { api, formatApiError } from "../lib/api";
import { Trophy, Users, Building2, TrendingUp, Network } from "lucide-react";

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/admin/analytics/referrals")
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, []);

  return (
    <DashboardShell>
      <div className="space-y-8" data-testid="admin-page">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// 99 / Admin Analytics</div>
          <h1 className="text-3xl lg:text-4xl font-semibold mt-1">Referral Network</h1>
          <p className="text-[#94a3b8] mt-2 max-w-xl">
            Track which organizations are bringing the most charities and individuals onto the platform.
          </p>
        </div>

        {error && (
          <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded px-4 py-3" data-testid="admin-error">
            {error}
          </div>
        )}

        {!data && !error && (
          <div className="csc-card p-8 font-mono-tech text-xs uppercase tracking-[0.25em] text-[#94a3b8]">
            Loading analytics…
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
              {[
                { icon: Network, label: "Total invite links", value: data.totals.total_orgs_with_ref_code, color: "#00aaff" },
                { icon: TrendingUp, label: "Total conversions", value: data.totals.total_conversions, color: "#39ff14" },
                { icon: Users, label: "People referred", value: data.totals.people_conversions, color: "#00aaff" },
                { icon: Building2, label: "Orgs referred", value: data.totals.org_conversions, color: "#39ff14" },
              ].map(({ icon: Icon, label, value, color }) => (
                <div key={label} className="csc-card p-5">
                  <div className="flex items-center justify-between">
                    <Icon size={18} style={{ color }} />
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#94a3b8]">Live</span>
                  </div>
                  <div className="mt-4 text-3xl font-semibold" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                    {value}
                  </div>
                  <div className="text-xs text-[#94a3b8] mt-1">{label}</div>
                </div>
              ))}
            </div>

            <section className="csc-card overflow-hidden" data-testid="admin-leaderboard">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(0,170,255,0.18)]">
                <div className="flex items-center gap-2">
                  <Trophy size={16} className="text-[#39ff14]" />
                  <h2 className="font-semibold">Top Referrers</h2>
                </div>
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">
                  {data.top_referrers.length} orgs
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="font-mono-tech text-[10px] uppercase tracking-[0.18em] text-[#94a3b8] bg-[#050d1a]">
                    <tr>
                      <th className="text-left px-4 py-3">#</th>
                      <th className="text-left px-4 py-3">Organization</th>
                      <th className="text-left px-4 py-3">Code</th>
                      <th className="text-right px-4 py-3">People</th>
                      <th className="text-right px-4 py-3">Orgs</th>
                      <th className="text-right px-4 py-3">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_referrers.map((r, i) => (
                      <tr
                        key={r.organization_id}
                        className="border-t border-[rgba(0,170,255,0.18)] hover:bg-[#00aaff]/5"
                        data-testid={`leaderboard-row-${i}`}
                      >
                        <td className="px-4 py-3 font-mono-tech text-[#94a3b8]">{i + 1}</td>
                        <td className="px-4 py-3">
                          <div className="text-white">{r.organization_name}</div>
                          <div className="text-[11px] font-mono-tech text-[#94a3b8]">{r.sector} · {r.country}</div>
                        </td>
                        <td className="px-4 py-3 font-mono-tech text-[#00aaff]">{r.ref_code}</td>
                        <td className="px-4 py-3 text-right">{r.people_conversions}</td>
                        <td className="px-4 py-3 text-right">{r.org_conversions}</td>
                        <td className="px-4 py-3 text-right text-[#39ff14] font-semibold">{r.total_conversions}</td>
                      </tr>
                    ))}
                    {data.top_referrers.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-[#94a3b8] font-mono-tech text-xs uppercase tracking-wider">No referrers yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="csc-card" data-testid="admin-recent">
              <div className="px-6 py-4 border-b border-[rgba(0,170,255,0.18)] flex items-center justify-between">
                <h2 className="font-semibold">Recent Referrals</h2>
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">
                  Last {data.recent_referrals.length}
                </span>
              </div>
              <ul>
                {data.recent_referrals.length === 0 ? (
                  <li className="px-6 py-8 text-center text-[#94a3b8] font-mono-tech text-xs uppercase tracking-wider">
                    No referrals yet.
                  </li>
                ) : (
                  data.recent_referrals.map((r, i) => (
                    <li
                      key={`${r.type}-${r.id}-${i}`}
                      className="px-6 py-3 flex items-center justify-between border-t border-[rgba(0,170,255,0.18)] first:border-t-0 text-sm"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`font-mono-tech text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded ${r.type === "user" ? "bg-[#00aaff]/10 text-[#00aaff]" : "bg-[#39ff14]/10 text-[#39ff14]"}`}>
                          {r.type}
                        </span>
                        <span className="text-white">{r.name}</span>
                        <span className="text-[#94a3b8]">← {r.referred_by}</span>
                      </div>
                      <span className="font-mono-tech text-[10px] text-[#94a3b8]">{r.at?.slice(0, 16).replace("T", " ")}</span>
                    </li>
                  ))
                )}
              </ul>
            </section>
          </>
        )}
      </div>
    </DashboardShell>
  );
}
